import { PermissionFlags } from 'src/app/enums/permissionFlags.enum';
import { PERMISSION_NAMES } from 'src/app/shared/constants/permissionNames.constants';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';

/**
 * The Role Permissions column model (DASH-3179).
 *
 * This reverses the design-spec §3 "Treatment A" (View/Create/Edit/Delete, each writing
 * `…My | …Hierarchy`) back to the legacy four-column model, per Tandeep's call on PR #1598.
 * Treatment A was not merely a preference — it was a privilege-escalation bug: ticking "Edit"
 * granted `UpdateHierarchy`, silently giving every editing role the ability to act on records
 * owned by others. The four legacy columns keep that capability behind an explicit **Admin**
 * control and match the masks the live editor and the RBAC enforcer already produce.
 *
 * Four columns (exact legacy header strings, from `permissionNames.constants.ts`):
 *   Read           = ReadMy | ReadHierarchy   = 17   (hierarchy-wide view)
 *   Create/Update  = CreateMy | UpdateMy      =  6   (own records only)
 *   Delete         = DeleteMy                 =  8   (own records only)
 *   Admin          = all eight flags          = 255  (the ONLY control that grants *Hierarchy writes)
 *
 * Admin is a legacy select-all with exact-equality semantics:
 *   - ticking Admin sets the mask to exactly 255;
 *   - un-ticking Admin sets it to exactly 0 (it wipes Read/Create/Update/Delete too — preserved
 *     as-is; changing it would alter save outcomes for existing roles, so it is its own ticket);
 *   - while a mask is exactly 255 the other three columns render checked-and-disabled (no
 *     auto-check cascade, no "unchecking one drops Admin").
 *
 * IMPORTANT (cross-screen dependency): Admin's 255 mask is the ONLY thing that sets
 * `UpdateHierarchy`, which is the reachability path for Sample "un-approve"
 * (`sample-details.component.ts` → `canUnapprove`). Whatever the Admin column writes MUST keep
 * `UpdateHierarchy` set — hence Admin = exactly 255. Do not narrow it.
 */
export enum PermissionColumn {
  Read = 'Read',
  CreateUpdate = 'Create/Update',
  Delete = 'Delete',
  Admin = 'Admin',
}

/** Read column mask — ReadMy | ReadHierarchy (17): hierarchy-wide view. */
export const READ_MASK = PermissionFlags.ReadMy | PermissionFlags.ReadHierarchy;

/** Create/Update column mask — the fused CreateMy | UpdateMy (6): own records only. */
export const CREATE_UPDATE_MASK = PermissionFlags.CreateMy | PermissionFlags.UpdateMy;

/** Delete column mask — DeleteMy (8): own records only. */
export const DELETE_MASK = PermissionFlags.DeleteMy;

/** Admin column mask — every one of the eight flags (255): the legacy select-all. */
export const ALL_PERMISSIONS =
  PermissionFlags.ReadMy |
  PermissionFlags.CreateMy |
  PermissionFlags.UpdateMy |
  PermissionFlags.DeleteMy |
  PermissionFlags.ReadHierarchy |
  PermissionFlags.CreateHierarchy |
  PermissionFlags.UpdateHierarchy |
  PermissionFlags.DeleteHierarchy;

/** Objects whose only meaningful permission is Read (a visibility gate, not an entity). */
export const VIEW_ONLY_OBJECT_IDS: readonly string[] = [RolePermissionObjects.Pii, RolePermissionObjects.AiAnalystAccess];

export function isViewOnlyObject(objectId: string): boolean {
  return VIEW_ONLY_OBJECT_IDS.includes(objectId);
}

/** One column of the matrix: its stable key, its display label and the mask it reads/writes. */
export interface PermissionColumnDef {
  column: PermissionColumn;
  label: string;
  mask: number;
  description: string;
}

/**
 * The four columns in display order — Read · Create/Update · Delete · Admin. Labels come from
 * {@link PERMISSION_NAMES} so the exact legacy header strings live in exactly one place. Each
 * column carries a plain-language `description` (surfaced as a header tooltip) that spells out the
 * scope the mask actually grants — the read/write split is otherwise invisible from the label alone.
 */
export const PERMISSION_COLUMNS: readonly PermissionColumnDef[] = [
  { column: PermissionColumn.Read, label: PERMISSION_NAMES[0], mask: READ_MASK, description: 'View records across the whole hierarchy' },
  { column: PermissionColumn.CreateUpdate, label: PERMISSION_NAMES[1], mask: CREATE_UPDATE_MASK, description: 'Create and edit your own records' },
  { column: PermissionColumn.Delete, label: PERMISSION_NAMES[2], mask: DELETE_MASK, description: 'Delete your own records' },
  { column: PermissionColumn.Admin, label: PERMISSION_NAMES[3], mask: ALL_PERMISSIONS, description: 'Full control, including records owned by others' },
];

/**
 * True when a column is granted. Mirrors the legacy `hasPermission`: a mask of exactly 255 (Admin)
 * satisfies every column; otherwise every bit of the column's mask must be present (exact match, so
 * `CreateMy` alone does NOT satisfy the `Create/Update` column, which needs `CreateMy | UpdateMy`).
 */
export function hasColumn(permissions: number, columnMask: number): boolean {
  return permissions === ALL_PERMISSIONS || (permissions & columnMask) === columnMask;
}

/**
 * Toggle a column, mirroring the legacy `togglePermission` exactly:
 *  - Admin: 255 ⇄ 0 (ticking sets the whole mask to 255; un-ticking wipes it to 0);
 *  - any other column: XOR its mask, but ONLY while Admin is not held (a 255 mask is immutable
 *    except through the Admin column). Returns a new mask; the input is not mutated.
 */
export function toggleColumn(permissions: number, columnMask: number, isViewOnly = false): number {
  if (isViewOnly) {
    if (columnMask !== READ_MASK) {
      return permissions;
    }
    return hasColumn(permissions, READ_MASK) ? 0 : READ_MASK;
  }
  if (columnMask === ALL_PERMISSIONS) {
    return permissions === ALL_PERMISSIONS ? 0 : ALL_PERMISSIONS;
  }
  return permissions !== ALL_PERMISSIONS ? permissions ^ columnMask : permissions;
}

/**
 * Grant or revoke a column explicitly (used by the group-header bulk-set). Admin resolves to the
 * whole mask (255 / 0); any other column sets or clears its bits, but leaves a full-admin mask
 * untouched — only the Admin column can change a 255 mask. Returns a new mask.
 */
export function setColumn(permissions: number, columnMask: number, granted: boolean, isViewOnly = false): number {
  if (isViewOnly) {
    if (granted) {
      return columnMask === READ_MASK ? READ_MASK : permissions;
    }
    return columnMask === READ_MASK || columnMask === ALL_PERMISSIONS ? 0 : permissions;
  }
  if (columnMask === ALL_PERMISSIONS) {
    return granted ? ALL_PERMISSIONS : 0;
  }
  if (permissions === ALL_PERMISSIONS) {
    return permissions;
  }
  return granted ? permissions | columnMask : permissions & ~columnMask;
}

/**
 * Which columns are disabled for a given mask:
 *  - view-only objects (GLOBAL_Pii): only the Read column is meaningful — everything else disabled;
 *  - while Admin (255) is held: the other three render checked-and-disabled (Admin already grants them).
 *
 * Read, Create/Update and Delete are otherwise INDEPENDENT checkboxes — each is toggled on its own.
 * There is deliberately no "write requires Read" gating (dropped per Tandeep's call on PR #1598):
 * greying Create/Update and Delete out while the always-enabled Admin column stayed clickable read as
 * an inconsistency, so a role can now hold any of the four verbs on its own, exactly like the labels imply.
 */
export function isColumnDisabled(permissions: number, columnMask: number, isViewOnlyObject: boolean): boolean {
  if (isViewOnlyObject) {
    return columnMask !== READ_MASK;
  }
  return hasColumn(permissions, ALL_PERMISSIONS) && columnMask !== ALL_PERMISSIONS;
}

/**
 * Plain-language explanation of *why* a column is disabled, so the greyed-out state is never a
 * mystery in the UI (surfaced as a cell tooltip + appended to the aria-label). Returns a raw,
 * translatable string when the column is disabled, or null when it is interactive. The two
 * cases mirror {@link isColumnDisabled} exactly, in the same precedence order (view-only object,
 * then a non-Admin column suppressed while Admin is held).
 */
export function columnDisabledReason(permissions: number, columnMask: number, isViewOnlyObject: boolean): string | null {
  if (!isColumnDisabled(permissions, columnMask, isViewOnlyObject)) {
    return null;
  }
  if (isViewOnlyObject) {
    return 'Only the Read permission applies to this object';
  }
  return 'Already granted by the Admin permission';
}
